// tslint:disable:variable-name
import { Flooring } from '../flooring/Flooring'
import { Bidder } from '../enums'
import { Device, WindowSize } from '../helpers'
import { Experiment } from '../helpers/Experiment'
import { getQueryParam } from '../helpers/getQueryParam'
import Logger from '../helpers/Logger'
import { spread } from '../helpers/spread'
import { DiscrepancyValues } from '../prebidFacade/BidderSettings'
import { IBidRequest } from '../../typings/IPrebid'
import { deepcopy } from '../helpers/deepcopy'
import { MediaType } from '../enums/MediaType'
import { LiftTest } from '../liftTest/LiftTest'
import {
  RecipeCardTracker,
  RecipeCard
} from '../recipeCardTracker/RecipeCardTracker'

export enum StaticSlots {
  leaderboard_atf = 'LeaderboardAtf',
  leaderboard_btf = 'LeaderboardBtf',
  sidebar_atf = 'SidebarAtf',
  sidebar_btf = 'SidebarBtf',
  // recipe = 'Recipe',
  recipe_btf = 'Recipe',
  recipe_mobile = 'Recipe',
  comments = 'Comments',
  feed = 'Feed'
}

export enum DeviceTypes {
  mobile = 'mobile',
  desktop = 'desktop',
  both = 'both'
}

const USER_DEFINED_SLOT_MAX_MOBILE_WIDTH = 424

/**
 *
 * This class acts as an adapter for mapping the
 * current APIs interface to the interface I hope the
 * API will transition to in the future. For example, we have
 * an array of slot models. The API currently doesn't deliver that
 * but will hopefully do so in the future. Once it does, we will
 * be able to remove the transform here and use it straight
 * from the API. The rest of the codebase will not require any
 * changes because we built on top of this interface from the start.
 *
 * Anything marked private on this model is intended to be
 * deprecated in the future
 */

export default class WebModel {
  readonly optimize_desktop_pagespeed: boolean
  readonly customTargeting: object
  readonly S2S: boolean
  readonly cpmIndices?: { [adUnitId: string]: number }
  readonly country?: string
  readonly jtr_button_selector?: string
  readonly jtr_selector?: string

  // Bidders

  aol: boolean
  appnexus: boolean
  appnexusAst: boolean
  brealtime: boolean
  google: boolean
  gumgum: boolean
  indexExchange: boolean
  pubmatic: boolean
  triplelift: boolean
  uam: boolean

  discrepancies: DiscrepancyValues | null
  bannerDiscrepancies: DiscrepancyValues | null
  below_content: boolean
  displayBidShield: DiscrepancyValues | null
  readonly experiment: Experiment
  readonly slots: Array<ISlotModel>
  readonly bidders: Array<IBidderModel>
  readonly bidRequests: IBidRequests
  adhesion_close: boolean
  adhesion_light: boolean
  adhesion_mobile: boolean
  adhesion_tablet: boolean
  adhesion_desktop: boolean
  adunit: string
  amazon: boolean
  category: ICategories
  categories: Array<ICategories>
  ccpa: '1' | '0'
  ccpa_link_loc: string
  ccpa_modal_enabled: boolean
  custom_ccpa_text: string
  content_cba_mobile_buffer: number | undefined
  content_cba_desktop_buffer: number | undefined
  content_cba_mobile_limit: number | undefined
  content_cba_mobile_percentage: number
  content_cba_desktop_limit: number | undefined
  content_cba_desktop_percentage: number
  chicory: boolean
  cm36: boolean // disable content mobile 300x600 disable
  cb_dn: boolean // disable content btf native ('fluid')
  comments_selector: string
  comments_position: string
  comscore: boolean
  comscore_vertical: string
  content_selector: string | undefined
  content_selector_mobile: string | undefined
  content_buffer_begin: number
  content_buffer_end: number
  content_desktop_spacing: number
  content_stop_selector: string
  content_mobile_spacing: number
  content_require_text: number
  content_mobile: boolean
  content_desktop: boolean
  custom_cmp_header: string
  custom_css: string
  custom_cmp_color: string
  customPurposes: Array<{ id: number; name: string; description: string }>
  vendorList: Array<object>
  customTitle: string
  deactivated: boolean
  display_ads: boolean
  divbuster_exclusions: string
  enable_divbuster: boolean
  enable_listbuster: boolean
  feed_selector: string
  feed_position: InsertPosition
  floor: { [type in MediaType]: number }
  footer_branding: boolean
  footer_selector?: string
  ga_excluded_query: string | null
  gdpr: '0' | '1'
  gdpr_compliance: number
  gumgum_id: string
  gumgum_in_image: boolean
  gumgum_skip_first_image: boolean
  iabOptouts: string[]
  id: number
  isPrebid: boolean
  keywordSrc: string
  killswitch: boolean
  leaderboard: boolean
  leaderboard_atf_selector: string
  leaderboard_atf_position: string
  leaderboard_atf_disable_billboard: boolean
  leaderboard_btf_selector: string
  leaderboard_btf_position: string
  less_lazy_mode: boolean
  mediavine_comscore: boolean
  newRelicBidRequests: boolean
  optouts: Array<IOptouts>
  psas: Array<IPSA>
  optimize_mobile_pagespeed: boolean
  outstreamDiscrepancies: DiscrepancyValues | null
  outstream_density: 'off' | 'light' | 'medium' | 'high'
  owned: boolean
  pageIdSrc: string
  privacy_policy_link: string
  psa: boolean
  quantcast: boolean
  recipe_float: boolean
  recipe_position: string
  recipe_selector: string
  recipe_mobile_position: string
  recipe_mobile_selector: string
  recipe_instruction_density: 'default' | 'medium' | 'low' | 'none'
  recipe_slot_density: 'default' | 'medium' | 'low'
  ro_id: number
  samplingRate?: number
  st_d: string
  st_m: string
  sidebar_atf_position: string
  sidebar_atf_selector: string
  sidebar_btf_disable_sticky: boolean
  sidebar_btf_selector: string
  sidebar_btf_stop_selector: string
  sidebar_btf_sticky_offset: number
  sidebar_minimum_width: number
  slug: string
  testSite: boolean
  title: string
  triplelift_sensitive: boolean
  topLevelCategory: {
    id: number
    title: string
    slug: string
    parentId: number
    iabCode: string
  }
  urlFilters: Array<IUrlFilter>
  versionGroup: IVersionGroup
  videoBetas: IVideoBetaGroup[]
  videoDiscrepancies: DiscrepancyValues | null
  videoVersion: string
  videoSettings: IVideoSettings
  adhesionVideo: IVideo
  featuredVideo: IVideo
  wrapperSrc: string
  launcherUrl: string
  zergnet: boolean
  zergnet_content_position: InsertPosition
  zergnet_content_selector: string
  zergnet_id: number
  zergnet_traffic_id: number
  videoFloor: number
  optimize_short_form_content: boolean
  s2sBidders: Array<string> = []
  growSiteID: string
  growFavesEnabled: boolean
  dynamic_recipe_slots: boolean
  enable_automatic_recipe_selectors: boolean
  jtr_arrival_unit_enabled: boolean

  constructor(model: IWebViewModel) {
    model.indexExchange = model.index
    // this isn't togglable from dashboard (yet) so its hardcoded true
    model.S2S = true

    // localModelOverrides is mostly for Trellis who want to be able to submit
    // overrides to the model. Yes we want to overwrite model, because some Static
    // modules rely on that global for data.
    const localModel = window.$mediavine.web.localModel || {}
    const localModelOverrides = isObject(localModel) ? localModel : {}
    model = { ...model, ...localModelOverrides }
    window.$mediavine.web.model = model

    spread(model).onto(this)
    this.experiment = new Experiment(model.experiments || [])

    // Reshaping model to match a future interface from API
    this.slots = convertSiteModelToSlotModels(this)
    this.bidders = convertSiteModelToPartnersModel(model)
    this.customTargeting = getCustomTargeting()
    this.floor = {
      [MediaType.banner]: Flooring.getDisplayFloor(this),
      [MediaType.video]: Flooring.getVideoFloor(this),
      [MediaType.outstream]: Flooring.getOutstreamFloor(this)
    }

    const liftTest = LiftTest.new(model)
    Object.keys(this.bidRequests).forEach((adunit) => {
      if (liftTest.bidder && liftTest.isDisplay() && liftTest.isPrebid()) {
        this.bidRequests[adunit] = this.bidRequests[adunit].filter(
          ({ bidder }) => bidder !== liftTest.bidder
        )
      }

      this.bidRequests[adunit].unshift({
        bidder: Bidder.S2S,
        params: {}
      })
    })

    Logger.debug(this)
  }

  get disableContentBtfNative(): boolean {
    return this.cb_dn
  }
}

function validateAdUnitType(model: WebModel, adUnitId: string): boolean {
  const valid = {
    adhesion_desktop: model.adhesion_desktop,
    adhesion_mobile: model.adhesion_mobile,
    adhesion_tablet: model.adhesion_tablet,
    comments: !!model.comments_selector,
    feed: !!model.feed_selector,
    leaderboard_atf: model.leaderboard && !!model.leaderboard_atf_selector,
    leaderboard_btf: model.leaderboard && !!model.leaderboard_btf_selector,
    recipe_mobile:
      Device.isMobileOrTablet &&
      (!!model.recipe_mobile_selector || !!model.recipe_selector),
    recipe_btf: !Device.isMobileOrTablet && !!model.recipe_selector,
    sidebar_btf: !!model.sidebar_btf_selector,
    sidebar_atf: !!model.sidebar_atf_selector
  }

  return valid[adUnitId] || false
}

function convertSiteModelToSlotModels(model: WebModel): Array<ISlotModel> {
  const slots: Array<ISlotModel> = []
  slots.push(...getUserDefinedSlots(model))
  slots.push(...getStaticSlots(model))
  slots.push(...getRecipeSlots(model))
  slots.forEach((slot) => {
    configureSidebarBtf(slot, model), configureSidebarAtf(slot, model)
  })

  return slots
}

/**
 * If sidebar minimum width is not set, we must load sidebar ATF lazy
 * Otherwise, it may be at the footer and hurt viewability by loading
 * non-lazy.
 *
 * Setting the slotModel.lazy prevents ATF renderer from instantiating
 * the slot.
 */
function configureSidebarAtf(slot: ISlotModel, model: WebModel) {
  if (slot.adUnitId === 'sidebar_atf' && !model.sidebar_minimum_width) {
    slot.lazy = true
  }
}

/**
 * Hardcoded for sidebar_btf but in the future,
 * dashboard slot models will be able to assign these to any slot
 */

function configureSidebarBtf(slot: ISlotModel, model: WebModel) {
  // TODO: move this to the SidebarBtf model and test
  if (slot.adUnitId === 'sidebar_btf') {
    slot.stickyOffset = model.sidebar_btf_sticky_offset
    slot.sticky = !model.sidebar_btf_disable_sticky
    slot.stickyStopSelector = model.sidebar_btf_stop_selector
  }
  // Don't move this. Must happen before Renderers fire.
  slot.lazy = slot.adUnitId.indexOf('atf') === -1
}

function createDynamicRecipeSlot(
  selector: HTMLElement | string,
  insertPosition
): ISlotModel {
  return {
    adUnitId: 'dynamic',
    insertPosition,
    selector,
    lazy: true,
    type: 'Recipe'
  }
}

/**
 * Recipe slots have become dynamically created
 * based on the presence of a known Recipe Card.
 * When a recipe card isn't detected, we fall back
 * to the selectors set up in the dashboard.
 * @param model
 */
function getRecipeSlots(model: WebModel): Array<ISlotModel> {
  const slots: Array<ISlotModel> = []
  const { recipeCard } = new RecipeCardTracker()
  if (
    recipeCard === RecipeCard.Tasty &&
    model.enable_automatic_recipe_selectors
  ) {
    slots.push(...getTastySlots(model))

    return slots
  }

  if (
    recipeCard === RecipeCard.WPRecipeMaker &&
    model.enable_automatic_recipe_selectors
  ) {
    slots.push(...getWPRMSlots(model))

    return slots
  }

  if (recipeCard === RecipeCard.MvCreate) {
    const elements = document.querySelectorAll(
      '.mv_recipe_target, .mv-create-target-selector'
    )

    elements.forEach((el) =>
      slots.push(createDynamicRecipeSlot(el as HTMLElement, 'beforeEnd'))
    )
  }

  const slotId = Device.isMobileOrTablet ? 'recipe_mobile' : 'recipe_btf'
  if (validateAdUnitType(model, slotId)) {
    slots.push({
      adUnitId: slotId,
      type: 'Recipe',
      selector: model[`recipe_selector`],
      insertPosition: model[`recipe_position`] as InsertPosition,
      mobileSelector: model[`recipe_mobile_selector`],
      mobileInsertPosition: model[`recipe_mobile_position`] as InsertPosition
    })
  }

  return slots
}

/**
 * -- Automatic Slot Density --
 * default - 2 desktop / 3 mobile
 * medium - 1 desktop / 2 mobile
 * low - 1 desktop / 1 mobile
 */
const recipeDensityMap = {
  default: { desktop: 2, mobile: 3 },
  medium: { desktop: 1, mobile: 2 },
  low: { desktop: 1, mobile: 1 }
}
function getWPRMSlots({
  recipe_slot_density,
  recipe_instruction_density
}: WebModel): Array<ISlotModel> {
  const slots: Array<ISlotModel> = []
  const device = Device.isMobileOrTablet ? 'mobile' : 'desktop'
  const numSlots = recipeDensityMap[recipe_slot_density][device]

  if (Device.isMobileOrTablet) {
    slots.push(
      createDynamicRecipeSlot(
        '.wprm-recipe-instructions-container > .wprm-recipe-instructions-header',
        'beforebegin'
      )
    )
  }

  slots.push(
    createDynamicRecipeSlot('.wprm-recipe-ingredients-container', 'afterbegin')
  )

  if (recipe_instruction_density === 'none') {
    slots.push(
      createDynamicRecipeSlot('.wprm-recipe-instructions-container', 'afterend')
    )
  }

  // Reduce the numbers of slots depending on recipeDensity
  return slots.splice(0, numSlots)
}

function getTastySlots({
  recipe_slot_density,
  recipe_instruction_density
}: WebModel): Array<ISlotModel> {
  const slots: Array<ISlotModel> = []
  const device = Device.isMobileOrTablet ? 'mobile' : 'desktop'
  const numSlots = recipeDensityMap[recipe_slot_density][device]

  if (Device.isMobileOrTablet) {
    slots.push(
      /** The first child, plus each 3rd child after that will get a slot */
      createDynamicRecipeSlot(
        `.tasty-recipes-ingredients ul:nth-of-type(3n + 1),
        .tasty-recipes-ingredients ol:nth-of-type(3n + 1),
        .tasty-recipes-ingredients p:nth-of-type(3n + 1),
        .tasty-recipes-ingredients h4:nth-of-type(3n + 1)`,
        'afterend'
      )
    )
  }

  slots.push(
    createDynamicRecipeSlot('.tasty-recipes-ingredients', 'afterbegin')
  )

  if (recipe_instruction_density === 'none') {
    slots.push(
      createDynamicRecipeSlot('.tasty-recipes-instructions', 'afterend')
    )
  }

  // Reduce the numbers of slots depending on recipeDensity
  return slots.splice(0, numSlots)
}

function convertSiteModelToPartnersModel(
  model: IWebViewModel
): Array<IBidderModel> {
  const partners: Array<IBidderModel> = []
  Object.keys(Bidder).map((value) => {
    const displayName = Bidder[value]
    if (value === Bidder.roundel && model.index) {
      partners.push({
        name: displayName
      })
    }
    if (model[value]) {
      partners.push({
        name: displayName
      })
    }
  })

  return partners
}

function getCustomTargeting(): object {
  return window.$mediavine.web.customTargeting || {}
}

export function optimizingForMobilePagespeed(): boolean {
  if (
    !window.$mediavine ||
    !window.$mediavine.web ||
    !window.$mediavine.web.model ||
    window.$mediavine.web.disable_pagespeed ||
    getQueryParam('optimizeSpeed') === 'false'
  ) {
    return false
  }

  return (
    getQueryParam('optimizeSpeed') === 'true' ||
    (window.$mediavine.web.model.optimize_mobile_pagespeed &&
      Device.isMobileOrTablet)
  )
}

export function optimizingForDesktopPagespeed(): boolean {
  if (
    !window.$mediavine ||
    !window.$mediavine.web ||
    !window.$mediavine.web.model ||
    window.$mediavine.web.disable_pagespeed ||
    getQueryParam('optimizeSpeed') === 'false'
  ) {
    return false
  }

  return (
    getQueryParam('optimizeSpeed') === 'true' ||
    (window.$mediavine.web.model.optimize_desktop_pagespeed &&
      !Device.isMobileOrTablet)
  )
}

export interface IBidRequests {
  [adunit: string]: Array<IBidRequest>
}

export function scrubBidder(
  requests: IBidRequests,
  bidder: Bidder
): IBidRequests {
  const newRequests = deepcopy(requests)
  Object.keys(newRequests).forEach((slot) => {
    newRequests[slot] = newRequests[slot].filter((b) => b.bidder !== bidder)
  })

  return newRequests
}

function isObject(a: any): boolean {
  return !!a && a.constructor === Object
}

function getUserDefinedSlots({
  slots
}: Pick<WebModel, 'slots'>): Array<ISlotModel> {
  const formattedSlots: Array<ISlotModel> = []
  // Adds and formats user-defined slots
  slots.forEach(({ adunit, position, selector, deviceType }) => {
    const mobile = Device.isMobileOrTablet
    const windowSizeIsMobile =
      WindowSize.width <= USER_DEFINED_SLOT_MAX_MOBILE_WIDTH

    if (adunit && position) {
      if (deviceType && deviceType !== DeviceTypes.both) {
        const typeAndDeviceAreDifferent =
          (deviceType === DeviceTypes.desktop && windowSizeIsMobile) ||
          (deviceType === DeviceTypes.mobile && !windowSizeIsMobile)
        if (typeAndDeviceAreDifferent) {
          return
        }
      }

      let adUnitId: string
      let type: SlotClass
      const insertPosition = position
      const userDefined = true
      switch (adunit) {
        case 'leaderboard':
        case 'sidebar':
          adUnitId = `${adunit}_btf`
          type = StaticSlots[adUnitId]
          break
        case 'recipe':
          adUnitId = mobile ? `${adunit}_mobile` : `${adunit}_btf`
          type = StaticSlots[adUnitId]
          break
        case 'content':
          adUnitId = adunit
          // Content is purposely not part of StaticSlots due to its dynamic insertion
          type = 'Content'
          break
        case 'feed':
        default:
          adUnitId = adunit
          type = StaticSlots[adUnitId]
      }

      formattedSlots.push({
        adUnitId,
        insertPosition,
        selector,
        type,
        userDefined
      })
    }
  })

  return formattedSlots
}

function getStaticSlots(model: WebModel): Array<ISlotModel> {
  const slots: Array<ISlotModel> = []

  Object.keys(StaticSlots).map((slotId) => {
    const type = StaticSlots[slotId]
    /** Recipe is handled in its own block of logic */
    if (slotId.indexOf('recipe_') !== -1) {
      return
    }
    if (validateAdUnitType(model, slotId)) {
      try {
        slots.push({
          adUnitId: slotId,
          type: type as SlotClass,
          selector: model[`${slotId}_selector`],
          insertPosition: model[`${slotId}_position`],
          mobileSelector: model[`${slotId}_mobile_selector`],
          mobileInsertPosition: model[`${slotId}_mobile_position`]
        })
      } catch (e) {
        Logger.debug(`error creating slot ${type}`, e)
      }
    }
  })

  return slots
}
